import React, {
  createContext, useCallback, useContext, useEffect, useMemo,
} from 'react';
import { useParams } from 'react-router-dom';
import { Loader, Message } from 'rsuite';
import handleError from '../../api/apiErrorHandle';
import { savePartnerInfo, savePartnerWorkflowStatus } from '../../api/partner/partner';
import { PartnerInfo } from '../../api/partner/partnerApiTypes';
import { useStoreActions, useStoreState } from '../../store/hooks';
import { showSuccessMessage } from '../../utils/message';

type Tab = 'mpi' | 'platform' | 'feed';

interface PartnerSetupContext {
  partner: PartnerInfo & {
    partnerName: string;
    partnerCode: string;
  };
  tab: Tab;
  savePartner: (partner: Omit<PartnerInfo, 'partnerCode'>) => Promise<void>;
}

const Context = createContext<PartnerSetupContext | undefined>(undefined);

function usePartnerSetupContext(): PartnerSetupContext {
  const context = useContext(Context);

  if (context === undefined) {
    throw new Error('usePartnerSetupContext must be used within a Provider');
  }

  return context;
}

interface ProviderProps {
  children: React.ReactNode;
}

function PartnerSetupProvider({ children }: ProviderProps): React.ReactElement {
  const { partnerInfo, status } = useStoreState(state => state.partner);
  const { fetchPartnerInfo, setPartnerInfo } = useStoreActions(actions => actions.partner);
  const { currentUser } = useStoreState(state => state.user);
  const { tab = 'mpi' } = useParams<{ tab: Tab }>();

  const savePartner = useCallback(
    async (partner: Omit<PartnerInfo, 'partnerCode'>): Promise<void> => {
      const { workflowStatus, ...info } = partner;

      try {
        if (workflowStatus !== partnerInfo?.workflowStatus) {
          await savePartnerWorkflowStatus({ workflowStatus });
        }

        await savePartnerInfo(info);
        setPartnerInfo(partner);
        showSuccessMessage('Partner successfully saved.');
      } catch (error) {
        handleError(error);
      }
    },
    [partnerInfo, setPartnerInfo],
  );

  const context: PartnerSetupContext | undefined = useMemo(
    () => {
      if (!partnerInfo) {
        return undefined;
      }

      return {
        partner: {
          ...partnerInfo,
          partnerName: partnerInfo.partnerName ?? currentUser.partnerName,
          partnerCode: currentUser.partnerCode,
        },
        tab,
        savePartner,
      };
    },
    [partnerInfo, currentUser, tab, savePartner],
  );

  useEffect(() => {
    fetchPartnerInfo();
  }, [currentUser, fetchPartnerInfo]);

  if (status === 'error') {
    return <Message type="error" showIcon>Failed to load the partner setup.</Message>;
  }

  if (!context) {
    return <Loader />;
  }

  return (
    <Context.Provider value={context}>
      {children}
    </Context.Provider>
  );
}

export { PartnerSetupProvider, usePartnerSetupContext };
